<template>
  <div class="ti-page-start py2 mb2">
    <div class="lg-section px3">
      <h2 v-if="!loading" class="detail-header-title">
        Vendor Connect Admin<template v-if="vendor">: {{vendor.name}}</template>
      </h2>
    </div>

    <template v-if="!loading">
      <div v-if="canAccess" class="px2 pt3">
        <div class="ti-page" v-html="intro"></div>

        <h3 class="ti-h3 mb1">Enquiries</h3>
        <p>
          View customer
          <router-link :to="{ name: 'admin-enquiries', params: { id: objId } }" class="ti-link">
            enquiries
          </router-link>
        </p>

        <div>
          <h3 class="ti-h3 mb1">Baseline Information</h3>
          <p class="ti-note">
            Note: This information will be displayed in all Vendor Connect cells that
            you have purchased, unless more detailed information is provided.
          </p>
          <div>
            <div style="margin-top:4px;">
              <span class="ti-prop-label">General: </span>
              <span class="ti-prop-value">{{baseline.general}}</span>
            </div>
            <div style="margin-top:4px;">
              <span class="ti-prop-label">Vertical Expertise: </span>
              <span class="ti-prop-value">{{baseline.expertise}}</span>
            </div>
            <div style="margin-top:4px;">
              <span class="ti-prop-label">Relevant Links: </span>
              <span class="ti-prop-value">{{baseline.links}}</span>
            </div>
            <div style="margin-top:4px;">
              <span class="ti-prop-label">Key Contacts: </span>
              <span class="ti-prop-value">{{baseline.contacts}}</span>
            </div>
            <div style="margin-top:4px;">
              <span class="ti-prop-label"> ... </span>
            </div>
          </div>
          <div class="my2">
            <router-link class="btn btn-neutral"
                         :to="{name: 'admin-baseline', params: {id: objId}}">
              Edit
            </router-link>
          </div>
        </div>

        <h3 class="ti-h3 mb1">Purchased</h3>
        <!-- fix horizontal overflow on chrome -->
        <table style="position:relative; margin-left:-10px;">
          <thead>
            <tr>
              <th></th>
              <template v-for="vert in vertMatrix()">
                <router-link :key="vert.code"
                             tag="th" class="tbl-label"
                             :class="{ 'tbl-purchased': vert.purchased,
                                     'tbl-draft': vert.draft }"
                             :to="{name: 'admin-vert',
                                  params:{id: objId, code:vert.code}}">
                  {{vert.label}}
                </router-link>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tech in techMatrix()" :key="tech.code">
              <template>
                <router-link tag="th"
                             class="tbl-label"
                             :class="{ 'tbl-draft': tech.draft,
                                       'tbl-purchased': tech.purchased }"
                             :to="{name: 'admin-tech', params:{id: objId, code:tech.code}}">
                  {{tech.label}}
                </router-link>
              </template>

              <template v-for="cell in cellMatrix(tech.code)">
                <router-link v-if="cell.purchased"
                             tag="td" :key="cell.vert"
                             class="tbl-purchased tbl-value"
                             :class="{'tbl-draft': cell.draft}"
                             :style="{ color: textColor(cell) }"
                             :to="{name: 'admin-sp',
                                  params:{id: objId, code: `${tech.code}-${cell.vert}`}}">
                  {{cell.src}}
                </router-link>
                <td v-else :key="cell.vert"></td>
              </template>
            </tr>
          </tbody>
        </table>
        <div style="text-align:right;" class="my1">
          <span class="legend-symbol tbl-purchased"></span>
          <span class="px1">PURCHASED</span>
          <span class="ml2 legend-symbol tbl-draft"></span>
          <span class="px1">DRAFT</span>
        </div>

        <div class="clearfix">
          <div class="sm-col sm-col-6">
            <h3 class="ti-h3 mb1">Preview</h3>
            <p>
              <router-link class="ti-link" :to="{ name: 'connect-detail',
                                                params: { id: vendor.id },
                                                query: { preview: 1 } }">
                Preview
              </router-link>
              all content (including drafts)
            </p>
            <div>
              <p class="mt1 ti-p">
                Link to <em>Preview Page</em> can be shared
                with non-subscribers through a <em>private</em>
                <a class="ti-link" @click="showShareLink()"> link</a>.
              </p>
              <p class="ti-note">
                Note: <strong>Anyone</strong> with this URL link
                will be able to access its content.<br/>
                The link <b>expires</b> in 4 weeks.
              </p>
            </div>

            <div class="mt1">
              <p class="ti-p my1">
                Following users have permanent access to preview page:
              </p>
              <div v-if="previewUsers.length!==0" class="ml1">
                <ul style="list-style-type:square; list-style-position:inside;">
                  <li v-for="user in previewUsers" :key="user.email" class="mb1">
                    {{user.email}}
                    <span v-if="!user.registered" title="Email not registered">
                      <img src="/static/icons/warning-6-32.png" style="height:24px; width:24px" />
                    </span>
                    <button @click="removePreviewUser(user.adminId)" class="ml2 btn btn-danger">
                      remove
                    </button>
                  </li>
                </ul>
              </div>
              <div v-else>
                <p><em>No users</em></p>
              </div>
              <div class="mt1">
                <button @click="showAddUserModal()">Add User</button>
              </div>
            </div>
          </div>

          <div class="sm-col sm-col-6">
            <template v-if="isStaff">
              <h3 class="ti-h3 mb1">BO Edit</h3>
              <p>
                <a class="ti-link" :href="`/bo/connect/${vendor.connId}/edit`">
                  Edit on BO
                </a>
              </p>
            </template>
            <h3 class="ti-h3 mb1">Publish</h3>
            <p class="ti-note" v-html="publishText"></p>
            <div class="mt2 flex">
              <div>
                <button class="btn btn-primary" @click="publish()"
                        :disabled="publishing || !previousPublish">
                  Publish
                </button>
              </div>
              <div class="ml2" style="max-width:400px;">
                <span class="ti-note">
                  <span v-if="!previousPublish">
                    Currently not visible on Transforma Insights’ website.<br/>
                    Please email
                    <a href="mailto:vendorconnect@transformainsights.com">
                      vendorconnect@transformainsights.com
                    </a> to confirm that you want to make your profile visible.
                  </span>
                  <span v-else-if="previousPublish">
                    Last published on: {{new Date(previousPublish * 1000)}}
                  </span>
                  <span v-else>
                    Currently not published.
                  </span>
                </span>
              </div>
            </div>

            <div v-if="previousPublish" class="mt4">
              <button class="btn btn-danger pill" @click="unpublish()" :disabled="publishing">
                Unpublish
              </button>
              <span class="ml2 ti-note">
                Completely remove Vendor Connect visibility for end users.
              </span>
            </div>
          </div>
        </div>

      </div>
      <div v-else class="lg-section px2 pt3"> <!-- !canAccess -->
        <div class="ml3 mb2">
          <span class="ti-message ti-message-alert">
            {{error}}
          </span>
        </div>
      </div>
    </template> <!-- if loaded -->

    <Modal name="share-link"
           height="auto" :scrollable="true">
      <div class="p3">
        <h3 class="ti-h3">Vendor Connect - Preview Content Link</h3>
        <p class="mt2 p1 ti-p" style="border: 1px solid black">
          <code style="font-family: mono;">{{shareLink}}</code>
        </p>
        <div class="mt1">
          <button type="button"
                  v-clipboard="shareLink"
                  v-clipboard:success="shareLinkCopyMsgSet"
                  >
            Copy
          </button>
          <span v-if="shareLinkCopyMsg" class="pl2 ti-note">
            {{shareLinkCopyMsg}}
          </span>
        </div>
      </div>
    </Modal>

    <Modal name="user-add-modal"
           height="auto" :scrollable="true" width="600px">

      <div class="p3">
        <h3 class="ti-h3t">Add user to PREVIEW access list</h3>
        <p class="ti-note">
          User's email address used to register on site.
        </p>
        <div class="my2 ti-form">
          <div v-if="userAddStatus=='initial' || userAddStatus=='searching'">
            <div class="form-label">
              <label>Email address</label>
            </div>
            <div>
              <input class="form-input" v-model="userAddEmailInput"
                     :disabled="userAddStatus=='searching'" />
            </div>
            <div class="mt2">
              <input type="button" class="btn btn-primary form-input"
                     @click="userAddSearch()" value="Add"
                     :disabled="userAddStatus=='searching'"/>
            </div>
          </div>

          <div v-else>
            <div v-if="userAddStatus=='not-found'" class="mt1" style="color:red;">
              User <strong>"{{userAddEmail}}"</strong> is not registered.<br/>
              <p class="my1"><strong>{{userAddEmail}}</strong>
                must register to access the preview page.</p>
            </div>
            <div v-if="userAddStatus=='found'">
              <p class="my1">Added user <strong>"{{userAddEmail}}"</strong></p>
            </div>
            <div>
              <input type="button" class="btn btn-primary form-input" value="OK"
                     @click="hideAddUserModal()" />
            </div>
          </div>

        </div>
      </div>
    </Modal>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminHome',
  components: { },
  data() {
    return {
      loading: true, // first load of refMap
      publishing: false,
      canAccess: null,
      isStaff: null,
      error: null,
      objId: this.$route.params.id,
      vendor: null,
      intro: null,
      publishText: null,
      previousPublish: null, // int timestamp
      baseline: null,
      // definitions
      technologies: null, // [{value, label}]
      verticals: null, // [{value, label}]
      matrix: null, // {vert, tech, cell}
      previewUsers: null,

      // forms - modals
      shareLink: null,
      shareLinkCopyMsg: null, // flashed message after click on "copy" to clipboard btn
      userAddEmail: null, // email address
      userAddEmailInput: null,
      userAddStatus: null, // initial, searching, found, not-found
    };
  },

  computed: {
  },

  created() {
    const params = {};
    axios.get(`/api/connect-admin/${this.objId}`, { params })
      .then((res) => {
        this.vendor = res.data.vendor;
        this.canAccess = res.data.canAccess;
        this.isStaff = res.data.staff;
        this.error = res.data.error;
        this.previewToken = res.data.previewToken;
        this.intro = res.data.intro;
        this.publishText = res.data.publishText;
        this.previousPublish = res.data.published;
        this.visible = res.data.visible;
        this.baseline = res.data.baseline;
        this.technologies = res.data.technologies;
        this.verticals = res.data.verticals;
        this.matrix = res.data.matrix;
        this.previewUsers = res.data.previewUsers;
        this.loading = false;
      });
  },

  methods: {
    textColor(cell) {
      if (cell.src === ' vert ') {
        return '#FDC833';
      }
      if (cell.src === ' tech ') {
        return '#94C23D';
      }
      if (cell.src === ' tech  vert ') {
        return '#2222DD';
      }
      return null;
    },


    // return list of tech with purchased info
    techMatrix() {
      const result = [];
      for (const tech of this.technologies) {
        const base = { label: tech.label, code: tech.value };
        result.push(Object.assign(base, this.matrix.tech[tech.value]));
      }
      return result;
    },
    // return list of vert with purchased info
    vertMatrix() {
      const result = [];
      for (const vert of this.verticals) {
        const base = { label: vert.label, code: vert.value };
        result.push(Object.assign(base, this.matrix.vert[vert.value]));
      }
      return result;
    },

    // return list of all cells in a row
    // each cell is a dict of {purchased, vert, src, draft}
    cellMatrix(techCode) {
      const result = [];
      const vertCells = this.matrix.cell[techCode];
      for (const vert of this.verticals) {
        const cell = { vert: vert.value, purchased: false };
        if (vertCells && vert.value in vertCells) {
          cell.purchased = true;
          const cellData = vertCells[vert.value];
          cell.src = cellData.deleted ? null : 'specific';
          cell.draft = cellData.draft;
        }
        if (!cell.src) {
          cell.src = '';
          if (techCode in this.matrix.tech) {
            const cellTech = this.matrix.tech[techCode];
            if (cellTech.purchased) {
              cell.purchased = true;
            }
            cell.src = (cellTech.src && !cellTech.deleted) ? ' tech ' : '';
          }
          if (vert.value in this.matrix.vert) {
            const cellVert = this.matrix.vert[vert.value];
            if (cellVert.purchased) {
              cell.purchased = true;
            }
            cell.src += (cellVert.src && !cellVert.deleted) ? ' vert ' : '';
          }
        }
        result.push(cell);
      }
      return result;
    },

    publish() {
      this.publishing = true;
      const params = { form: 'publish' };
      axios.post(`/api/connect-admin/${this.objId}`, {}, { params })
        .then((res) => {
          this.publishing = false;
          if (res.data.result === 'ok') {
            this.purchased = res.data.purchased;
            this.previousPublish = res.data.published;
          } else {
            let errorMsg = 'Error';
            if (res.data.msg) {
              errorMsg = res.data.msg;
            }
            alert(errorMsg);
          }
        });
    },

    unpublish() {
      this.publishing = true;
      const params = { form: 'unpublish' };
      axios.post(`/api/connect-admin/${this.objId}`, {}, { params })
        .then((res) => {
          this.publishing = false;
          if (res.data.result === 'ok') {
            this.purchased = res.data.purchased;
            this.previousPublish = res.data.published;
          } else {
            let errorMsg = 'Error';
            if (res.data.msg) {
              errorMsg = res.data.msg;
            }
            alert(errorMsg);
          }
        });
    },

    showShareLink() {
      const route = this.$router.resolve({
        name: 'connect-detail',
        params: { id: this.vendor.id },
        query: { preview: 1, token: this.previewToken },
      });
      this.shareLink = window.location.origin + route.href;
      this.$modal.show('share-link');
    },
    shareLinkCopyMsgSet() {
      this.shareLinkCopyMsg = 'Copied to clipboard';
      const that = this;
      function resetMsg() {
        that.shareLinkCopyMsg = null;
      }
      window.setTimeout(resetMsg, 3000);
    },

    // user PREVIEW management
    removePreviewUser(adminId) {
      const postData = {
        __op__: 'remove',
        id: adminId,
      };
      const params = { form: 'user' };
      axios.post(`/api/connect-admin/${this.objId}`, postData, { params })
        .then((res) => {
          this.previewUsers = this.previewUsers.filter(
            (item) => !(item.adminId === res.data.id),
          );
        });
    },
    showAddUserModal() {
      this.userAddStatus = 'initial';
      this.userAddEmail = '';
      this.userAddEmailInput = '';
      this.$modal.show('user-add-modal');
    },
    hideAddUserModal() {
      this.userAddStatus = 'initial';
      this.$modal.hide('user-add-modal');
    },
    userAddSearch() {
      this.userAddStatus = 'searching';
      this.userAddEmail = this.userAddEmailInput;
      const postData = { email: this.userAddEmail };
      const params = { form: 'user' };
      axios.post(`/api/connect-admin/${this.objId}`, postData, { params })
        .then((res) => {
          if (res.data.result === 'found') {
            this.userAddStatus = 'found';
            this.userAddEmailInput = '';
            this.previewUsers.push(res.data.user);
          } else if (res.data.result === 'not-found') {
            this.userAddStatus = 'not-found';
            this.previewUsers.push(res.data.user);
          }
        });
    },
  },
};
</script>

<style scoped>
table {
  border-collapse: collapse;
}

.tbl-label {
  cursor: pointer;
}

table, th, td {
  border-collapse: collapse;
  border: 1px solid #BDBDBD;
}

.tbl-purchased {
  background-color: #41c0f024;
  font-weight: bold;
  cursor: pointer;
}

.tbl-draft {
   outline: solid orange;
}

.legend-symbol {
  display:inline-block;
  width:20px;
  height:10px;
}
</style>
